/* Búsqueda global B2B (UX #2, jul 2026) — Cmd/Ctrl+K desde cualquier
 * pantalla. Pensada para el agente en plena venta: productos para
 * cotizar, sus cotizaciones para compartir/continuar y sus reservas. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from './ui/command';
import { Dialog, DialogContent } from './ui/dialog';
import { MagnifyingGlass, MapTrifold, FileText, Suitcase, LinkSimple } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

export const GlobalSearch = () => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const timer = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!open) { setQ(''); setResults(null); }
  }, [open]);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults(null); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/api/search/global`, {
          params: { q: q.trim() }, withCredentials: true,
        });
        setResults(r.data);
      } catch { setResults(null); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [q]);

  const go = useCallback((url) => {
    setOpen(false);
    navigate(url);
  }, [navigate]);

  const copyPublic = (e, publicUrl) => {
    e.stopPropagation();
    navigator.clipboard.writeText(`${window.location.origin}${publicUrl}`);
    toast.success('Link de la cotización copiado');
    setOpen(false);
  };

  const hasResults = results && (results.products?.length || results.quotes?.length || results.bookings?.length);

  return (
    <>
      <button
        type="button"
        className="pjr-btn-icon"
        title="Buscar (Ctrl/Cmd + K)"
        onClick={() => setOpen(true)}
        data-testid="nav-global-search"
      >
        <MagnifyingGlass size={19} weight="bold" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="overflow-hidden p-0 max-w-xl" data-testid="global-search-dialog">
          <Command shouldFilter={false} className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]]:px-2 [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-2 [&_[cmdk-item]]:py-3">
        <CommandInput
          placeholder="Buscar productos, cotizaciones o reservas…"
          value={q}
          onValueChange={setQ}
          data-testid="global-search-input"
        />
        <CommandList data-testid="global-search-results">
          {q.trim().length < 2 && (
            <div className="py-6 text-center text-[12.5px] text-muted-foreground">
              Escribe el nombre de un cliente, destino o código (CT-…, BK-…)
            </div>
          )}
          {q.trim().length >= 2 && !loading && !hasResults && (
            <CommandEmpty>Sin resultados para «{q}»</CommandEmpty>
          )}
          {loading && (
            <div className="py-6 text-center text-[12.5px] text-muted-foreground">Buscando…</div>
          )}
          {results?.products?.length > 0 && (
            <CommandGroup heading="Productos y circuitos">
              {results.products.map((p, i) => (
                <CommandItem key={`p${i}`} value={`p${i}`} onSelect={() => go(p.url)} data-testid={`gs-product-${i}`}>
                  <MapTrifold size={15} className="mr-2 shrink-0 text-pjr-blue" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-medium">{p.title}</div>
                    <div className="truncate text-[11px] text-muted-foreground">{p.subtitle}</div>
                  </div>
                  <span className="ml-2 shrink-0 text-[10px] font-bold uppercase text-emerald-700">Cotizar →</span>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {results?.quotes?.length > 0 && (
            <CommandGroup heading="Mis cotizaciones">
              {results.quotes.map((sq, i) => (
                <CommandItem key={`q${i}`} value={`q${i}`} onSelect={() => go(sq.url)} data-testid={`gs-quote-${i}`}>
                  <FileText size={15} className="mr-2 shrink-0 text-amber-600" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-medium">{sq.title}</div>
                    <div className="truncate text-[11px] text-muted-foreground">{sq.subtitle || sq.quote_id}</div>
                  </div>
                  {sq.public_url && (
                    <button
                      type="button"
                      onClick={(e) => copyPublic(e, sq.public_url)}
                      className="ml-2 inline-flex shrink-0 items-center gap-1 rounded border border-border px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground hover:bg-muted"
                      data-testid={`gs-quote-copy-${i}`}
                    >
                      <LinkSimple size={11} /> Copiar link
                    </button>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {results?.bookings?.length > 0 && (
            <CommandGroup heading="Mis reservas">
              {results.bookings.map((b, i) => (
                <CommandItem key={`b${i}`} value={`b${i}`} onSelect={() => go(b.url)} data-testid={`gs-booking-${i}`}>
                  <Suitcase size={15} className="mr-2 shrink-0 text-indigo-600" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13px] font-medium">{b.title}</div>
                    <div className="truncate text-[11px] text-muted-foreground">{b.ref || b.booking_id} · {b.subtitle}</div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
          </Command>
        </DialogContent>
      </Dialog>
    </>
  );
};
